import {AlertIcon, InfoIcon} from '@primer/octicons-react'
import {Box, Button, Flash, Octicon, Text, useOnOutsideClick} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type {FormEvent} from 'react'
import {useMemo, useRef, useState} from 'react'
import {useDragAndDrop} from '../hooks/use-drag-and-drop'
import {debounceAnnouncement, successfulMoveAnnouncement} from '../utils/announcements'
import {DragAndDropMovingResources, DragAndDropResources} from '../utils/strings'
import {DragAndDropMoveOptions, type DragAndDropItem} from '../utils/types'
import {announce} from '@github-ui/aria-live'
import {MoveModalForm} from './MoveModalForm'
import {testIdProps} from '@github-ui/test-id-props'

const formStyles: BetterSystemStyleObject = {display: 'flex', flexDirection: 'column', overflow: 'hidden'}

const dialogBodyStyles: BetterSystemStyleObject = {
  display: 'flex',
  gap: 3,
  overflowY: 'auto',
  flexDirection: 'column',
}

interface DragAndDropMoveModalProps {
  title: string
  currentIndex: number
  closeDialog: (close: boolean) => void
}

const errorMessage = (items: DragAndDropItem[], index?: number) => {
  if (index === undefined) {
    return DragAndDropResources.entryIsRequired
  }
  if (index < 0) {
    return DragAndDropResources.entryLessThanOne
  }
  if (index > items.length) {
    return DragAndDropResources.entryGreaterThanList(items.length)
  }

  return DragAndDropResources.entryIsInvalid
}

const isInvalid = (items: DragAndDropItem[], index?: number) =>
  index === undefined || index < 0 || index >= items.length || isNaN(index)

/**
 * A modal that allows the user to move an item to a new position in the list.
 *
 * @param title The title of the item being moved
 * @param currentIndex The current index of the item being moved
 * @param closeDialog Callback to close the dialog
 */
export const DragAndDropMoveModal = ({title, currentIndex, closeDialog}: DragAndDropMoveModalProps) => {
  const [index, setIndex] = useState<number | undefined>()
  const [dropdownSelection, setDropdownSelection] = useState(DragAndDropMoveOptions.BEFORE)
  const [moveHelpText, setMoveHelpText] = useState(`${title} will be moved ...`)
  const [invalidPosition, setInvalidPosition] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const {moveToPosition, items} = useDragAndDrop()
  const [error, setError] = useState<string | undefined>()

  const updateHelperText = useMemo(
    () => (idx?: number) => {
      let message = ''
      // invalid position

      if (idx === undefined) {
        setInvalidPosition(false)
        message = `${title} will be moved ...`
      } else if (isInvalid(items, idx)) {
        setInvalidPosition(true)
        message = DragAndDropMovingResources.cannotBeMoved(title)
      } else {
        setInvalidPosition(false)
        if (idx === currentIndex && dropdownSelection !== DragAndDropMoveOptions.AFTER) {
          message = DragAndDropMovingResources.itemWillNotBeMoved(title)
        } else if (idx === 0) {
          message = DragAndDropMovingResources.movedToFirst(title)
        } else if (idx === items.length - 1 && dropdownSelection !== DragAndDropMoveOptions.BEFORE) {
          message = DragAndDropMovingResources.movedToLast(title)
        } else {
          const itemBefore = items[idx - 1]?.title
          const itemAfter = items[idx + 1]?.title
          if (dropdownSelection === DragAndDropMoveOptions.AFTER) {
            message = DragAndDropMovingResources.movedBetween(title, items[idx]?.title, itemAfter)
          } else {
            message = DragAndDropMovingResources.movedBetween(title, itemBefore, items[idx]?.title)
          }
        }
      }
      setMoveHelpText(message)
      debounceAnnouncement(message)
    },
    [title, items, dropdownSelection, currentIndex],
  )

  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault() // prevent page reload
    if (index === undefined || isInvalid(items, index)) {
      setError(errorMessage(items, index))
      return
    }
    switch (dropdownSelection) {
      case DragAndDropMoveOptions.ROW:
        if (index >= currentIndex) moveToPosition(currentIndex, index, false)
        else moveToPosition(currentIndex, index, true)
        break
      case DragAndDropMoveOptions.BEFORE:
        moveToPosition(currentIndex, index, true)
        break
      case DragAndDropMoveOptions.AFTER:
      default:
        moveToPosition(currentIndex, index, false)
        break
    }
    closeDialog(false)
    successfulMoveAnnouncement({
      newIndex: index,
      currentIndex,
      items,
      title,
    })
  }

  // Dialog doesn't use Overlay or call `useOnOutsideClick` under the hood. This means that
  // it doesn't get registered as an overlay in the stack, which means it doesn't intercept
  // and stop propagating clicks. This means that if there are any open `Overlay`s underneath
  // this `Dialog`, clicks in this dialog will register as clicks outside of those overlays,
  // causing them to try to close.
  // We can prevent this by registering a fake `useOnClickOutside` hook to make this look like
  // a regular `Overlay`. Clicks inside will no-op and the hook will prevent them from propagating
  // to other overlays; clicks outside need to have `preventDefault` called on them to stop them
  // from propagating.
  useOnOutsideClick({containerRef, onClickOutside: e => e.preventDefault()})

  return (
    <Dialog
      title={
        <Box as="span" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          Move selected item
        </Box>
      }
      onClose={() => {
        closeDialog(false)
        announce(DragAndDropResources.cancelMove(title), {assertive: true})
      }}
      width="large"
      ref={containerRef}
      renderBody={() => (
        // By default there is no way to wrap the footer and body in one element, so we
        // have to custom-render the footer inside the body. Otherwise we can't use `submit`
        // button to submit the form
        <Box
          as="form"
          onSubmit={onSubmit}
          {...testIdProps('move-modal-form')}
          // Stopping blur propagation fixes a bug where the table thinks the cell editor was
          // blurred when it was really an input inside this dialog.
          onBlur={e => e.stopPropagation()}
          sx={formStyles}
          noValidate
        >
          <Dialog.Body sx={dialogBodyStyles}>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
              <Text sx={{fontWeight: 'bold'}}>Item</Text>
              <Text>{title}</Text>
            </Box>
            <MoveModalForm
              options={items}
              onChange={i => {
                setIndex(i)
                setError(undefined)
                updateHelperText(i)
              }}
              initialPosition={currentIndex + 1}
              dropdownSelection={dropdownSelection}
              setDropdownSelection={setDropdownSelection}
              error={error}
            />
            <Flash variant={invalidPosition ? 'warning' : 'default'}>
              <Octicon icon={invalidPosition ? AlertIcon : InfoIcon} sx={{color: 'fg.accent'}} />
              {moveHelpText}
            </Flash>
          </Dialog.Body>

          <Dialog.Footer sx={{padding: 2}}>
            <Button type="submit" variant="primary" {...testIdProps('drag-and-drop-move-modal-move-item-button')}>
              Move item
            </Button>
          </Dialog.Footer>
        </Box>
      )}
    />
  )
}

try{ DragAndDropMoveModal.displayName ||= 'DragAndDropMoveModal' } catch {}